import { ApiError } from "@groundspark/browser-auth";
import { type StreetRecord } from "./domain";

/** A conflict stays blocked until an explicit authoritative refresh succeeds. */
export class StreetState {
  public record: StreetRecord | null;
  public conflict = false;
  public busy = false;
  public error: unknown = null;
  private generation = 0;
  private active = true;
  public constructor(
    initial: StreetRecord | null,
    private readonly load: () => Promise<StreetRecord>,
    private readonly persist: (completed: boolean, revision: number) => Promise<StreetRecord>,
    private readonly changed: () => void,
  ) {
    this.record = initial;
  }
  public async refresh(manual = false): Promise<void> {
    if (!this.active || this.busy || (!manual && this.conflict)) return;
    const generation = ++this.generation;
    this.busy = true;
    this.changed();
    try {
      const record = await this.load();
      if (!this.active || generation !== this.generation) return;
      this.record = record;
      this.error = null;
      this.conflict = false;
    } catch (error) {
      if (this.active && generation === this.generation) this.error = error;
    } finally {
      if (this.active && generation === this.generation) {
        this.busy = false;
        this.changed();
      }
    }
  }
  public async toggle(): Promise<void> {
    if (!this.active || this.busy || this.conflict || !this.record) return;
    const generation = ++this.generation;
    const record = this.record;
    this.busy = true;
    this.error = null;
    this.changed();
    try {
      const saved = await this.persist(!record.completed, record.revision);
      if (this.active && generation === this.generation) this.record = saved;
    } catch (error) {
      if (this.active && generation === this.generation) {
        this.error = error;
        this.conflict = error instanceof ApiError && error.status === 409;
      }
    } finally {
      if (this.active && generation === this.generation) {
        this.busy = false;
        this.changed();
      }
    }
  }
  public dispose(): void {
    this.active = false;
    this.generation += 1;
    this.record = null;
  }
}
